<template>
	<div class="page-wrapper">
		<offline-block v-if="showBlock=='offline'" />
		<auth-block v-if="showBlock=='auth'" />
		<router-view v-if="showBlock=='main'" />
		<div v-if="showBlock=='loader'" class="global-loader">
			<loader />
		</div>
	</div>
</template>

<script setup lang="ts">
import { ref, onMounted, computed } from 'vue';
import { useRoute } from 'vue-router';
import { loadUserData } from '../classes/user';
import AuthBlock from '../components/blocks/auth-block.vue';
import OfflineBlock from '../components/blocks/offline-block.vue';
import { isLanguageInPath, setupLanguage } from '../classes/translations';
import { router } from '../routes';
import { useStore } from '@/js/stores/index';

const store = useStore()
const route = useRoute()

// Reactves
const pwaInstalled = ref(false)

// Computeds
const currentLanguage = computed(() => store.currentLanguage)
const settings = computed(() => store.settings)
const authenticated = computed(() => store.authenticated)
const showOffline = computed(() => store.offline)
const showBlock = computed(() => {
	switch(true){
		case showOffline.value:
			return 'offline';
		case authenticated.value === false:
			return 'auth';
		case !!settings.value:
			return 'main';
		default:
			return 'loader';
	}
})

// Lifecycle
onMounted(async () => {
	// Load owner data if needed
	store.owner = { // Let's set it immediately to have id in other components ASAP
		id: settings.value.owner_id,
		image: null
	}
	
	// Initialize and set language
	setupLanguage()

	// Lets figure out if the user is logged in
	await loadUserData()

	// Wait for router to resolve routes.
	await router.isReady()

	// Check if we have the language in the path and redirect respectively
	if(!isLanguageInPath(settings.value.languagesSupported, route.path)){
		await router.replace({
			path: "/" + currentLanguage.value + route.path,
			query: route.query // Pass query string, it may containg params like utm_source, utm_medium
		})
	}
})

// Offline/online state handling
window.addEventListener('offline', () => store.offline = true)
window.addEventListener('online', () => store.offline = false)

// Detect if we're in PWA
if (window.matchMedia('(display-mode: standalone)').matches || ('standalone' in window.navigator && (window.navigator as any).standalone === true)) {
	// User is currently navigating on the PWA so yes it's installed
	pwaInstalled.value = true
} else {
	// User is navigating in browser
	window.addEventListener('beforeinstallprompt', () => {
		pwaInstalled.value = false
		// User can get an installation prompt meaning the app is not installed
	})

	window.addEventListener('appinstalled', () => { // Deprecated by standards
		pwaInstalled.value = true
	})
}
</script>